import type {FetchResult} from "./types/fetch-result.interface";
import {AxisDataService} from "./types/axis-data-service.interface";
import type {IndexStrategy} from "./types/index-strategy.interface";
import {StandardIndexStrategy} from "./strategies/standard-index-strategy";
import type {ExpandResult} from "./types/expand-result.interface";
import {RangeFetchCoordinator} from "./range-fetch-coordinator";

/**
 * Internal interface for tracking boundary discovery state
 */
interface BoundaryState {
    /** Known minimum boundary index (relative to origin) */
    min: number;
    /** Known maximum boundary index (relative to origin) */
    max: number;
    /** True if min boundary is confirmed (hasMoreBefore = false from API) */
    minConfirmed: boolean;
    /** True if max boundary is confirmed (hasMoreAfter = false from API) */
    maxConfirmed: boolean;
}

/**
 * Represents a matrix axis with capabilities for managing and navigating indexed data.
 *
 * @template TValue The type of the values stored in the axis.
 * @template TPrimitive The primitive type to which the values can be converted for indexing purposes.
 */
export class Axis<TValue, TPrimitive extends PropertyKey = TValue & PropertyKey> {

    private readonly strategy: IndexStrategy<TPrimitive>;

    private readonly valueCache: Map<number, TValue> = new Map();

    /**
     * Expansions currently running, keyed by the value being loaded. Lets concurrent
     * ensureValue() calls for the same value share one expansion instead of each starting
     * their own. Entries are removed as soon as the expansion settles, success or failure.
     */
    private readonly inFlightExpansions: Map<TPrimitive, Promise<number>> = new Map();

    /**
     * Coordinates in-flight range fetches: dedup tracking, the tracked-promise
     * lifecycle, the read API for missing-range detection, and awaiting fetches
     * overlapping a requested range. Wired via injected callbacks (executor +
     * synchronous apply step); cache/boundary mutation stays owned by this Axis.
     */
    private readonly coordinator: RangeFetchCoordinator<TValue>;

    /**
     * Axis absolute boundaries (discovered lazily from fetch responses)
     *
     * - [ -∞, +∞] = Unknown (infinite scroll)
     * - [-12, +10] = Both absolute boundaries are known (finite axis)
     */
    private _absoluteBoundaries!: Promise<[number, number]>;

    /**
     * Resolver for the absoluteBoundaries promise.
     * Called when boundaries are discovered from fetch responses.
     */
    private _resolveBoundaries!: (boundaries: [number, number]) => void;

    /**
     * Tracks boundary discovery state from fetch responses.
     * Boundaries are lazily discovered via hasMoreBefore/hasMoreAfter flags.
     */
    private _boundaryState: BoundaryState = {
        min: -Infinity,
        max: Infinity,
        minConfirmed: false,
        maxConfirmed: false
    };

    /**
     * Promise that resolves when axis initialization is complete.
     *
     * Initialization performs boundary discovery ONLY (a single minimal fetch via
     * discoverBoundariesViaFetch). No values are eagerly preloaded (PRD-026 lazy
     * loading) — consumers load the visible range on demand through ensureRange().
     * This holds for both finite and infinite axes.
     */
    private readonly _ready: Promise<void>;

    /**
     * Retrieves the matrix axis identifier associated with the current object.
     *
     * @return {string} The identifier of the matrix axis.
     */
    get axisId(): string {
        return this._axisId;
    }

    /**
     * Retrieves the absolute boundaries as a promise resolving to a tuple containing two numeric values.
     * These values represent the minimum and maximum boundaries defined for a specific context.
     *
     * @return {Promise<[number, number]>} A promise that resolves to a tuple of two numbers, representing the minimum and maximum boundaries.
     */
    get absoluteBoundaries(): Promise<[number, number]> {
        return this._absoluteBoundaries;
    }

    /**
     * Retrieves the loaded boundaries for the current instance.
     *
     * @return {[number, number]} A tuple representing the loaded boundaries where the first number indicates the lower boundary and the second number indicates the upper boundary.
     */
    get loadedBoundaries(): [number, number] {
        if (this.valueCache.size === 0) {
            return [0, 0];
        }

        const keys = Array.from(this.valueCache.keys());

        return [Math.min(...keys), Math.max(...keys)];
    }

    /**
     * Promise that resolves when axis initialization is complete.
     *
     * Resolves after boundary discovery completes (a single minimal fetch). Values
     * are NOT preloaded eagerly (PRD-026 lazy loading); the origin is the only
     * cached entry at this point. Consumers must call ensureRange() to load the
     * visible range on demand. Applies to both finite and infinite axes.
     *
     * @return {Promise<void>} A promise that resolves when the axis is ready for use.
     */
    get ready(): Promise<void> {
        return this._ready;
    }

    /**
     * Synchronous access to absolute boundaries.
     * Returns [-Infinity, Infinity] for unconfirmed boundaries.
     * As boundaries are discovered from fetch responses, the known bounds are returned.
     *
     * @returns Tuple [min, max] representing axis boundaries
     */
    getBoundaries(): [number, number] {
        return [
            this._boundaryState.minConfirmed ? this._boundaryState.min : -Infinity,
            this._boundaryState.maxConfirmed ? this._boundaryState.max : Infinity
        ];
    }

    /**
     * Read-only capability flag: whether this axis' AxisDataService supports
     * expanding the loaded range to include an arbitrary missing value.
     *
     * `expandToValue` is optional on AxisDataService, so a service that omits it is a
     * legitimate state (not a data error). Callers can query this BEFORE invoking
     * `ensureValue()` — which throws for a missing value when the service lacks
     * `expandToValue` — to skip gracefully instead of triggering that throw. This does
     * NOT expose the private `dataService`, and does NOT change `ensureValue`'s contract.
     *
     * @returns {boolean} true if the AxisDataService implements `expandToValue`, else false.
     */
    get canExpandToValue(): boolean {
        return !!this.dataService.expandToValue;
    }

    /**
     * Creates an instance of the class with the specified axis identifier, origin value, data service, and optional value converter.
     *
     * @param {string} _axisId - The unique identifier for the axis.
     * @param {TValue} origin - The initial value representing the origin of the axis.
     * @param {AxisDataService<TValue>} dataService - The service to provide data for the axis.
     * @param {(value: TValue) => TPrimitive} [converter] - An optional function to convert the axis value to a primitive type.
     */
    constructor(
        private readonly _axisId: string,
        private readonly origin: TValue,
        private readonly dataService: AxisDataService<TValue>,
        private readonly converter?: (value: TValue) => TPrimitive
    ) {
        if (!dataService) {
            throw new Error('Cannot discover absoluteBoundaries: No dataService configured.');
        }

        this.strategy = new StandardIndexStrategy(this.toPrimitive(origin));
        this.valueCache.set(0, origin);

        // Wire the range-fetch coordinator via composition: the executor binds the
        // origin into the dataService call, and applyResult runs the Axis-owned
        // synchronous post-fetch step (boundary update THEN cache insert).
        this.coordinator = new RangeFetchCoordinator<TValue>(
            (offset, limit) => this.dataService.fetchValues(this.origin, offset, limit),
            (result, offset) => {
                this.updateBoundariesFromFetchResult(result, offset);
                this.insertAt(offset, ...result.values);
            }
        );

        // Initialize deferred boundary promise - resolved when boundaries discovered from fetch
        this.initializeBoundaryPromise();

        // Ready promise: trigger minimal initial fetch (limit=1, offset=0) to discover boundaries.
        // This breaks the deadlock by ensuring fetchValues is called BEFORE waiting on _absoluteBoundaries.
        // The response's boundaries property (if present) will immediately resolve _absoluteBoundaries.
        //
        // Lazy loading (PRD-026): the axis does NOT eagerly preload values on construction.
        // The _ready chain resolves once boundary discovery completes; consumers load the
        // visible range on demand via ensureRange(). This keeps construction cheap for both
        // finite and infinite axes and avoids fetching the entire axis up front.
        this._ready = this.discoverBoundariesViaFetch()
            .then(() => {
                // No eager preload — boundary discovery is sufficient for readiness.
            })
            .catch((err) => {
                console.error(`Axis ${this._axisId} initialization failed:`, err);
                throw err;
            });
    }

    /**
     * Discovers axis boundaries by triggering a minimal fetch (limit=1, offset=0).
     * The backend response should include boundaries in the FetchResult.
     * This method waits for boundaries to be discovered before returning.
     *
     * @returns Promise resolving to [min, max] boundaries
     */
    private async discoverBoundariesViaFetch(): Promise<[number, number]> {
        // Trigger minimal fetch to get boundaries from response
        // Offset 0 with limit 1 is the smallest possible request
        const result = await this.dataService.fetchValues(this.origin, 0, 1);

        // Update boundaries from the fetch result
        // This will resolve _absoluteBoundaries if boundaries are included
        this.updateBoundariesFromFetchResult(result, 0);

        // Wait for boundaries to be fully resolved
        // (they should already be resolved if boundaries were in the response)
        return this._absoluteBoundaries;
    }

    /**
     * Initializes the deferred boundary promise.
     * The promise is resolved when boundaries are discovered from fetch responses.
     */
    private initializeBoundaryPromise(): void {
        this._absoluteBoundaries = new Promise<[number, number]>((resolve) => {
            this._resolveBoundaries = resolve;
        });
    }

    /**
     * Convert TValue to TPrimitive for Map key equality
     */
    private toPrimitive(value: TValue): TPrimitive {
        if (this.converter) {
            return this.converter(value);
        }

        // Ohne converter muss TValue bereits ein PropertyKey sein
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'symbol') {
            return value as unknown as TPrimitive;
        }

        throw new Error(
            'Axis.toPrimitive(): No converter provided, but TValue is not a PropertyKey. ' +
            'Provide a converter, e.g. DateTime -> number (toMillis) or string (toISO).'
        );
    }

    /**
     * Check if an index is loaded in the cache.
     *
     * @param index - Index to check
     * @returns true if the index exists in cache
     */
    hasIndex(index: number): boolean {
        return this.valueCache.has(index);
    }

    /**
     * Check if a value exists in the cache.
     *
     * @param value - Value to check
     * @returns true if the value exists in cache
     */
    hasValue(value: TValue): boolean {
        return this.strategy.getIndex(this.toPrimitive(value)) !== undefined;
    }

    /**
     * Get value at index from cache
     *
     * @param index - Index to retrieve
     * @returns Value at index, or undefined if not loaded
     */
    getValue(index: number): TValue | undefined {
        return this.valueCache.get(index);
    }

    /**
     * Get index for a value from cache
     *
     * @param value - Value to look up
     * @returns Index of value, or undefined if not found
     */
    getIndex(value: TValue): number | undefined {
        return this.strategy.getIndex(this.toPrimitive(value));
    }

    /**
     * Get the number of values
     */
    size(): number {
        return this.strategy.size();
    }

    /**
     * Ensure index range is loaded via AxisDataService.
     *
     * This method checks which indices are missing from the cache and only fetches
     * those ranges from the backend. Consecutive missing indices are grouped into
     * ranges and fetched in parallel for optimal performance.
     *
     * Boundaries are automatically discovered from the fetch response's
     * hasMoreBefore/hasMoreAfter flags - no separate boundary API call needed.
     *
     * Uses Origin (Index 0) as reference point for relative fetching.
     *
     * @param startIndex - Start index (can be negative)
     * @param endIndex - End index (exclusive)
     *
     * @example
     * ```typescript
     * const yAxis = new Axis('bob-uuid', resourceService);
     * // Origin 'bob-uuid' is at Index 0
     *
     * await yAxis.ensureRange(-3, 0);  // Load 3 resources BEFORE origin
     * await yAxis.ensureRange(0, 10);  // Load 10 resources AFTER origin
     *
     * // Second call with overlapping range - only fetches missing indices
     * await yAxis.ensureRange(5, 15);  // Only fetches indices 10-14
     * ```
     */
    async ensureRange(startIndex: number, endIndex: number): Promise<void> {
        // Clip range to known boundaries (skip requests beyond confirmed bounds)
        const clippedRange = this.clipRangeToBoundaries(startIndex, endIndex);
        if (clippedRange === null) {
            return; // Entire range is outside confirmed boundaries
        }

        // Extend to the edge of the loaded block so the cache stays gap-free
        const [effStart, effEnd] = this.extendRangeToLoadedEdge(clippedRange[0], clippedRange[1]);

        // Start a tracked fetch for every still-missing sub-range
        const missingRanges = this.mergeCloseRanges(this.findMissingRanges(effStart, effEnd));
        for (const [gapStart, gapEnd] of missingRanges) {
            this.coordinator.startFetch(gapStart, gapEnd);
        }

        // Resolve only once every fetch overlapping this range has loaded its data
        await this.coordinator.awaitOverlapping(effStart, effEnd);
    }

    /**
     * Extends the requested range to the edge of the already-loaded contiguous
     * block so the value cache never develops a hole between the loaded region
     * and the new request (gap-free invariant). No-op when nothing is cached.
     *
     * @param start - Clipped start index (inclusive)
     * @param end - Clipped end index (exclusive)
     * @returns The effective [start, end) range to load
     */
    private extendRangeToLoadedEdge(start: number, end: number): [number, number] {
        if (this.valueCache.size === 0) {
            return [start, end];
        }

        const [loadedMin, loadedMax] = this.loadedBoundaries;
        const effStart = start > loadedMax + 1 ? loadedMax + 1 : start;
        const effEnd = end < loadedMin ? loadedMin : end;

        return [effStart, effEnd];
    }

    /**
     * Merges ranges that are close together to reduce API calls.
     * Two ranges are merged if the gap between them is small (1 index).
     * This optimizes the common case where only the origin is cached,
     * preventing splits like [-10,0) and [1,3) into separate requests.
     *
     * @param ranges - Array of [start, end) ranges to potentially merge
     * @returns Merged ranges where small gaps have been combined
     */
    private mergeCloseRanges(ranges: Array<[number, number]>): Array<[number, number]> {
        if (ranges.length <= 1) {
            return ranges;
        }

        const merged: Array<[number, number]> = [];
        let current = ranges[0];

        for (let i = 1; i < ranges.length; i++) {
            const next = ranges[i];
            const gapSize = next[0] - current[1];

            // Merge if gap is small (only 1 cached index between ranges)
            // This handles the origin-split case without over-merging larger gaps
            if (gapSize <= 1) {
                current = [current[0], next[1]];
            } else {
                merged.push(current);
                current = next;
            }
        }

        merged.push(current);
        return merged;
    }

    /**
     * Updates the boundary state from a fetch response.
     * Supports two modes:
     * 1. Fast path: If boundaries are provided directly in the response, use them immediately
     * 2. Discovery path: When hasMoreBefore/hasMoreAfter is false, we've discovered a boundary
     *
     * @param result - The fetch result containing boundary flags and optional boundaries
     * @param requestedOffset - The offset that was requested
     */
    private updateBoundariesFromFetchResult(
        result: FetchResult<TValue>,
        requestedOffset: number
    ): void {
        // Fast path: If boundaries are provided directly, use them immediately
        // This enables instant boundary discovery without multiple fetches
        if (result.boundaries) {
            this._boundaryState.min = result.boundaries.min;
            this._boundaryState.max = result.boundaries.max;
            this._boundaryState.minConfirmed = true;
            this._boundaryState.maxConfirmed = true;

            const boundaries: [number, number] = [result.boundaries.min, result.boundaries.max];
            this._resolveBoundaries(boundaries);
            return;
        }

        // Fallback: Discover boundaries from hasMoreBefore/hasMoreAfter flags
        const valuesCount = result.values.length;

        // Discover MIN boundary: if hasMoreBefore is false, we hit the start
        if (!result.hasMoreBefore && !this._boundaryState.minConfirmed) {
            // The actual min is where values started
            // If we requested offset -10 and got 3 values, min is -10 + (10 - 3) = offset
            // Actually: if hasMoreBefore is false, the first returned value IS the boundary
            this._boundaryState.min = requestedOffset;
            this._boundaryState.minConfirmed = true;
        }

        // Discover MAX boundary: if hasMoreAfter is false, we hit the end
        if (!result.hasMoreAfter && !this._boundaryState.maxConfirmed) {
            // The actual max is the last index that has a value
            // offset + valuesCount - 1 is the last valid index
            this._boundaryState.max = requestedOffset + valuesCount - 1;
            this._boundaryState.maxConfirmed = true;
        }

        // If both boundaries are confirmed, resolve the absoluteBoundaries promise
        if (this._boundaryState.minConfirmed && this._boundaryState.maxConfirmed) {
            const boundaries: [number, number] = [
                this._boundaryState.min,
                this._boundaryState.max
            ];
            this._resolveBoundaries(boundaries);
        }
    }

    /**
     * Clips the requested range to known boundaries.
     * Prevents unnecessary API calls for ranges outside confirmed bounds.
     *
     * @param startIndex - Requested start index
     * @param endIndex - Requested end index (exclusive)
     * @returns Clipped [start, end] range, or null if entirely outside bounds
     */
    private clipRangeToBoundaries(startIndex: number, endIndex: number): [number, number] | null {
        let clippedStart = startIndex;
        let clippedEnd = endIndex;

        // Only clip to confirmed boundaries (don't clip to unconfirmed infinite bounds)
        if (this._boundaryState.minConfirmed && Number.isFinite(this._boundaryState.min)) {
            clippedStart = Math.max(clippedStart, this._boundaryState.min);
        }

        if (this._boundaryState.maxConfirmed && Number.isFinite(this._boundaryState.max)) {
            // endIndex is exclusive, max is inclusive, so clip to max + 1
            clippedEnd = Math.min(clippedEnd, this._boundaryState.max + 1);
        }

        // If range is invalid after clipping, nothing to fetch
        if (clippedStart >= clippedEnd) {
            return null;
        }

        return [clippedStart, clippedEnd];
    }

    /**
     * Ensure a specific value is loaded in the axis cache.
     * If the value is already cached, returns its index immediately.
     * Otherwise, calls AxisDataService.expandToValue() to load the value
     * and all values between the current boundaries and the missing value.
     *
     * Concurrent calls for the SAME value share one expansion. The provider queues up to four
     * ensureValue() calls per entry with no cross-entry deduplication, and entries routinely
     * share a date — the reported incident alone had three items starting on the same day. For
     * an in-memory service that would only be wasted work; for a service that goes over the
     * network it would be one request per caller. This mirrors what RangeFetchCoordinator
     * already does for range fetches.
     *
     * KNOWN LIMIT: this joins identical values only. Two DIFFERENT missing values on the same
     * side of the window still start two expansions, even though the further one already
     * covers the nearer one — expanding to 2026-07-20 subsumes 2026-08-03. Harmless for an
     * in-memory service (the result is the same mapping either way, see the invariant on
     * AxisDataService.expandToValue), but a network-backed service issues both requests. If
     * that load ever shows up in practice, the fix is to join by DIRECTION and keep the widest
     * pending expansion, not to key the map differently.
     *
     * @param value - The value to ensure is loaded
     * @returns The index of the value in the axis
     * @throws Error if expandToValue is not supported by the AxisDataService
     * @throws Error if the value does not exist (propagated from AxisDataService)
     */
    async ensureValue(value: TValue): Promise<number> {
        // Fast path: value already cached
        const existingIndex = this.getIndex(value);
        if (existingIndex !== undefined) {
            return existingIndex;
        }

        // Join an expansion for the same value that is already running.
        const key = this.toPrimitive(value);
        const inFlight = this.inFlightExpansions.get(key);
        if (inFlight) {
            return inFlight;
        }

        const expansion = this.performExpansion(value)
            .finally(() => this.inFlightExpansions.delete(key));
        this.inFlightExpansions.set(key, expansion);

        return expansion;
    }

    /**
     * Performs the actual expansion for {@link ensureValue}. Split out so the in-flight
     * bookkeeping above stays readable and has exactly one place to clear itself.
     *
     * Deliberately NOT named `expandToValue`: that name belongs to the AxisDataService method
     * this calls, which has a different signature. Two same-named methods with different
     * contracts would make every grep for the name ambiguous.
     *
     * @param value - The value to load
     * @returns The index of the value in the axis
     */
    private async performExpansion(value: TValue): Promise<number> {

        // Check if expandToValue is supported
        if (!this.dataService.expandToValue) {
            throw new Error(
                `expandToValue is not supported by AxisDataService '${this.dataService.axisDataServiceId}'. ` +
                `Cannot load missing value.`
            );
        }

        // Get loaded boundary values
        const [minIdx, maxIdx] = this.loadedBoundaries;
        const minValue = this.getValue(minIdx);
        const maxValue = this.getValue(maxIdx);

        if (minValue === undefined || maxValue === undefined) {
            throw new Error(
                'Cannot expand: loaded boundaries have no cached values. ' +
                'Ensure ensureRange() was called before ensureValue().'
            );
        }

        // Call expandToValue on the AxisDataService. The origin travels as the third argument
        // so the service can return an origin-relative insertAtIndex without holding state.
        const result: ExpandResult<TValue> = await this.dataService.expandToValue(
            [minValue, maxValue],
            value,
            this.origin
        );

        // Insert the returned values into the cache
        this.insertAt(result.insertAtIndex, ...result.values);

        // Return the index of the requested value
        const newIndex = this.getIndex(value);
        if (newIndex === undefined) {
            throw new Error(
                `Value was not found in cache after expandToValue. ` +
                `The AxisDataService did not include the requested value in its response.`
            );
        }

        return newIndex;
    }

    /**
     * Finds consecutive ranges of indices that are not present in the value cache
     * and are not currently being fetched (per the coordinator's coversIndex read API).
     *
     * Iterates over the specified range [start, end) and identifies gaps where
     * indices are missing from both cache AND in-flight coordinator fetches. Consecutive
     * missing indices are grouped into ranges represented as [startIndex, endIndex] tuples.
     *
     * This prevents duplicate API calls when multiple concurrent ensureRange()
     * calls request overlapping ranges.
     *
     * @param start - The starting index of the range to check (inclusive)
     * @param end - The ending index of the range to check (exclusive)
     * @returns An array of tuples, each representing a missing range [startIndex, endIndex]
     *
     * @example
     * ```typescript
     * // Cache contains indices: 0, 1, 2, 5, 6
     * // Pending fetch for indices: 3, 4
     * findMissingRanges(0, 10);
     * // Returns: [[7, 10]] (3-4 are pending, not included)
     * ```
     */
    private findMissingRanges(start: number, end: number): Array<[number, number]> {
        // Guard against infinite ranges
        if (!Number.isFinite(start) || !Number.isFinite(end)) {
            return [];
        }

        const missing: Array<[number, number]> = [];
        let gapStart: number | null = null;

        for (let i = start; i < end; i++) {
            // Index is "covered" if in cache OR currently being fetched
            const isCovered =
                this.valueCache.has(i) ||
                this.coordinator.coversIndex(i);

            if (!isCovered) {
                if (gapStart === null) {
                    gapStart = i;
                }
            } else {
                if (gapStart !== null) {
                    missing.push([gapStart, i]);
                    gapStart = null;
                }
            }
        }

        if (gapStart !== null) {
            missing.push([gapStart, end]);
        }

        return missing;
    }

    /**
     * Inserts one or more values starting at the specified index.
     *
     * @param {number} startIndex - The index at which the values should be inserted.
     * @param {...TValue[]} values - The values to be inserted.
     * @return {void} No return value.
     */
    insertAt(startIndex: number, ...values: TValue[]): void {
        const items = values.map((value, i) => {
            const idx = startIndex + i;
            this.valueCache.set(idx, value);

            return {
                key: this.toPrimitive(value),
                index: idx
            };
        });
        this.strategy.insertAt(items);
    }
}